/**
 * Widget Context Provider
 *
 * Provides widget configuration and utilities to all child components.
 */

import React, { createContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { useSessionKeepAlive } from './hooks/useSessionKeepAlive';
import { useEhrMediator } from './hooks/useEhrMediator';
import { AtlasUIProvider } from '@wellsky/atlas-ui-react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import type { ResolvedWidgetConfig, WidgetConfig, WidgetImperativeRef } from './types';
import { resolveConfig } from './utils/configuration';
import { ApiClientContext } from '../contexts/ApiClientContext';
import { createWidgetWorkItemsApi } from './services/widgetWorkItemsApi';

/**
 * Widget context value interface
 */
export interface WidgetContextValue {
  /** Resolved widget configuration */
  config: ResolvedWidgetConfig;
  /** Function to update configuration */
  updateConfig: (newConfig: Partial<WidgetConfig>) => void;
  /** Portal container for Radix UI components */
  portalContainer: ShadowRoot | HTMLElement | null;
  /** Function to trigger data refresh */
  refresh: () => void;
}

/**
 * Widget Context
 */
export const WidgetContext = createContext<WidgetContextValue | null>(null);

/**
 * Portal Context for Radix UI components
 */
export const PortalContext = createContext<ShadowRoot | HTMLElement | null>(null);

interface WidgetProviderProps {
  /** Initial widget configuration */
  config: ResolvedWidgetConfig;
  /** Portal container for Radix UI components (usually shadowRoot) */
  portalContainer?: ShadowRoot | HTMLElement | null;
  /** Imperative ref for external method calls (e.g. setAuthToken) */
  imperativeRef?: WidgetImperativeRef;
  /** Child components */
  children: React.ReactNode;
}

/**
 * Widget Provider Component
 *
 * Wraps the widget content with necessary providers:
 * - WidgetContext for configuration
 * - QueryClientProvider for data fetching
 * - PortalContext for Radix UI portals
 */
export function WidgetProvider({ config: initialConfig, portalContainer, imperativeRef, children }: WidgetProviderProps) {
  const [config, setConfig] = useState<ResolvedWidgetConfig>(initialConfig);

  // Create a stable QueryClient instance
  const queryClientRef = useRef<QueryClient | null>(null);
  if (!queryClientRef.current) {
    queryClientRef.current = new QueryClient({
      defaultOptions: {
        queries: {
          staleTime: config.features.refreshInterval,
          refetchOnWindowFocus: false,
          retry: 1,
        },
      },
    });
  }

  // --- EHR Mediator integration ---

  // Session keep-alive: send postMessages to parent on user activity
  useSessionKeepAlive({
    enabled: config.enableSessionKeepAlive,
  });

  // Token refresh handler: update auth config when parent sends new token
  const handleTokenRefresh = useCallback((newToken: string) => {
    setConfig((prev) => ({
      ...prev,
      auth: {
        ...prev.auth,
        token: newToken,
      },
    }));
  }, []);

  // Listen for EHR Mediator messages (token refresh, session expiry)
  useEhrMediator({
    onTokenRefresh: handleTokenRefresh,
    onSessionExpired: config.onSessionExpired,
    parentOrigin: config.auth?.parentOrigin,
  });

  // Wire imperative ref so external callers can update React state
  useEffect(() => {
    if (imperativeRef) {
      imperativeRef.current.setAuthToken = handleTokenRefresh;
    }
    return () => {
      if (imperativeRef) {
        imperativeRef.current.setAuthToken = null;
      }
    };
  }, [imperativeRef, handleTokenRefresh]);

  // No need for portal container setup - it comes from props (shadowRoot)

  // Update configuration
  const updateConfig = useCallback((newConfig: Partial<WidgetConfig>) => {
    setConfig((prev) => resolveConfig({ ...prev, ...newConfig }));
  }, []);

  // Refresh data
  const refresh = useCallback(() => {
    queryClientRef.current?.invalidateQueries();
  }, []);

  // Context value
  const contextValue: WidgetContextValue = {
    config,
    updateConfig,
    portalContainer: portalContainer || null,
    refresh,
  };

  // Create widget-specific API client that uses configured apiBaseUrl
  const widgetApi = useMemo(() => createWidgetWorkItemsApi(config), [config]);

  // API client value for context
  const apiClientValue = useMemo(() => ({
    getWorkItem: async (workItemId: string) => {
      return widgetApi.getWorkItem(workItemId);
    },
    dismissWorkItem: async (workItemId: string, reason?: string) => {
      return widgetApi.dismissWorkItem(workItemId, reason);
    },
  }), [widgetApi, config.apiBaseUrl]);

  return (
    <AtlasUIProvider defaultTheme="light" loadFonts={false}>
      <WidgetContext.Provider value={contextValue}>
        <QueryClientProvider client={queryClientRef.current}>
          <ApiClientContext.Provider value={apiClientValue}>
            <PortalContext.Provider value={portalContainer || null}>
              {children}
            </PortalContext.Provider>
          </ApiClientContext.Provider>
        </QueryClientProvider>
      </WidgetContext.Provider>
    </AtlasUIProvider>
  );
}
